import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../../services/api';

/**
 * Quién del equipo de la empresa ve su Facturación (docs/facturacion-equipo-plan.md).
 *
 * Apagado por defecto. Encendido, el operador o el gestor ve lo mismo que la
 * empresa, pero solo de los expedientes que tiene asignados. El servidor decide
 * (`services/billing/billingAccess.js`); esto solo lo configura.
 *
 * `companyId` es la empresa del perfil: así el admin lo puede tocar por un
 * cliente. El gestor solo existe en AR, así que su casilla sale solo con AR.
 */
const TeamBillingAccess = ({ companyId, isAdmin = false, hasAr = false }) => {
  const [datos, setDatos] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/team-permissions', { params: isAdmin ? { company_id: companyId } : {} });
      setDatos(data.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, [companyId, isAdmin]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const alternar = async role => {
    const nuevo = !datos.billing[role];
    setGuardando(true);
    try {
      const { data } = await api.put('/team-permissions', {
        ...(isAdmin ? { company_id: companyId } : {}),
        billing: { [role]: nuevo },
      });
      setDatos(data.data);
      toast.success(nuevo ? 'Ya pueden ver la facturación de sus expedientes' : 'Ya no ven la facturación');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  if (!datos) return null;

  const roles = [
    { role: 'operator', label: 'Los operadores ven la facturación de sus expedientes' },
    ...(hasAr ? [{ role: 'gestor', label: 'Los gestores ven la facturación de sus expedientes' }] : []),
  ];

  return (
    <div className='bg-white rounded-lg border border-slate-200 p-6'>
      <div className='flex items-center mb-1'>
        <BanknotesIcon className='h-5 w-5 text-blue-600 mr-2' />
        <h3 className='text-lg font-medium text-slate-900'>Facturación del equipo</h3>
      </div>
      <p className='text-sm text-slate-500 mb-4'>
        Por defecto solo la ves tú. Si lo enciendes, cada uno ve lo mismo que tú en Facturación, pero solo de los
        expedientes que tiene asignados.
      </p>
      <div className='divide-y divide-slate-100'>
        {roles.map(r => (
          <label key={r.role} className='flex items-center justify-between gap-3 py-2 text-sm text-slate-700'>
            {r.label}
            <input
              type='checkbox'
              checked={Boolean(datos.billing?.[r.role])}
              disabled={guardando || !datos.can_edit}
              onChange={() => alternar(r.role)}
              aria-label={r.label}
            />
          </label>
        ))}
      </div>
    </div>
  );
};

export default TeamBillingAccess;
